import { useTranslation } from "react-i18next"
import clsx from "clsx"

import type { RelatedArticlesQueryArgs } from "~/api/prismic/articles/getRelatedArticles"
import { useRelatedArticlesQuery } from "~/api/prismic/articles/getRelatedArticles"
import { linkResolver } from "~/api/prismic/linkResolver"
import { ButtonLink } from "~/components/Link/ButtonLink"
import { PublishedItemPreview } from "~/components/PublishedItemPreview"
import { PageRoutes } from "~/enums/PageRoutes"
import * as RichText from "~/utils/prismic/richText"
import { getClassesByColorScheme } from "~/utils/style/colorSchemes"

export type RelatedArticlesProps = RelatedArticlesQueryArgs

export const RelatedArticles: React.FC<RelatedArticlesProps> = ({
  articleId,
  categoryIds,
}) => {
  const { t } = useTranslation("articlePage")

  const { data: relatedArticles = [] } = useRelatedArticlesQuery({
    articleId,
    categoryIds,
  })

  const colorSchemeClasses = getClassesByColorScheme({})

  if (!relatedArticles.length) return null

  return (
    <aside className="sm:my-4 md:my-8">
      <div className="mx-auto max-w-screen-lg py-8 px-4">
        <h2
          className={clsx(
            "mb-4 inline-flex bg-primary-500 px-2 text-2xl",
            colorSchemeClasses.bg,
          )}
        >
          {t("sidebar.relatedArticles.title")}
        </h2>
        <ul
          className={clsx("my-4 grid items-stretch gap-4", {
            "md:grid-cols-2 md:gap-6": relatedArticles.length <= 2,
            "md:grid-cols-3": relatedArticles.length === 3,
          })}
        >
          {relatedArticles.map((a) => {
            return (
              <li key={a.id} className="flex flex-col">
                <article className="grow">
                  <PublishedItemPreview
                    title={RichText.asText(a.data.title)}
                    description={a.data.meta_description}
                    image={a.data.banner_image}
                    date={a.first_publication_date}
                    href={linkResolver(a)}
                  />
                </article>
              </li>
            )
          })}
        </ul>

        <div className="mb-4 flex flex-col items-center">
          <ButtonLink href={PageRoutes.ARTICLES_LIST}>
            {t("sidebar.relatedArticles.seeAll")}
          </ButtonLink>
        </div>
      </div>
    </aside>
  )
}
